/* eslint-disable react-hooks/exhaustive-deps */
import classNames from 'classnames'
import styles from './ReportsFilter.module.css'
import { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { commercialCarsSelector } from '../../../redux/selectors/cars-selector'
import { usersSelector } from '../../../redux/selectors/users-selector'
import { useOutsideClick } from '../../../hooks/useOutsideClick'
import {
  setSelectedDrivers,
  setSelectedTrucks,
  setSelectedStatus,
  setSelectedUsersCommon,
  setSelectedStatusCommon,
} from '../../../redux/reducers/report-filter'
import {
  REPORT_STATUS_FILTER_ITEMS,
  REPORT_STATUS_FILTER_LABELS,
  REPORT_COMMON_STATUS_FILTER_ITEMS,
} from '../../../utils/reportStatus'

interface Option {
  id: number
  name: string
}

interface DropdownProps {
  title: string
  isOpen: boolean
  setIsOpen: any
  selectedItems: any
  items: any[]
  handleChange: any
  handleSelectAll: any
  handleDeselectAll: any
  setSelectedDrivers?: any
  getReports?: any
  allMeansEmpty?: boolean
  columnClassName?: string
}

const Dropdown = ({
  title,
  isOpen,
  setIsOpen,
  selectedItems,
  items,
  handleChange,
  handleSelectAll,
  handleDeselectAll,
  getReports,
  allMeansEmpty = false,
}: DropdownProps) => {
  const ref = useOutsideClick(() => {
    setIsOpen(false)
  })

  const [itemsAfterUpdate, setItemsAfterUpdate] = useState<any[]>()

  useEffect(() => {
    //@ts-ignore
    const updatedItems = items.map((item) => {
      if (!item.active) {
        return { ...item, name: `${item.name} (disabled)` }
      }
      return item
    })
    setItemsAfterUpdate(updatedItems)
  }, [items])

  const selectAll = () => {
    handleSelectAll(allMeansEmpty ? [] : itemsAfterUpdate)
  }

  const deselectAll = () => {
    handleDeselectAll()
  }

  const isAllSelected = allMeansEmpty
    ? selectedItems.length === 0
    : selectedItems.length === (itemsAfterUpdate?.length ?? items.length) &&
      (itemsAfterUpdate?.length ?? items.length) > 0

  return (
    <div className="col-12 col-sm-4 col-md-4 col-lg-4 col-xl-4">
      <div className="d-lg-block mb-1">{title}</div>
      <div
        ref={ref}
        onClick={() => setIsOpen((prev: boolean) => !prev)}
        className={classNames(
          styles.dropdown,
          styles.dropdownMultipleSelect,
          'd-block',
          isOpen && styles.opened
        )}
        style={{ textAlign: 'left' }}
      >
        <div
          className={classNames(
            styles.btn,
            styles.btnSmall,
            styles.btnWide,
            styles.btnInput,
            styles.btnDropdown
          )}
        >
          {selectedItems.length
            ? selectedItems.map(({ name }: any) => name).join(', ')
            : `Все ${title}`}
        </div>
        <div
          className={styles.dropdownContent}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ textAlign: 'left' }}>
            <input
              className={styles.input}
              type="checkbox"
              name={`filter_${title.toLowerCase()}_all`}
              id={`${title.toLowerCase()}_all`}
              checked={isAllSelected}
              onChange={(e) => {
                if (e.target.checked) {
                  selectAll()
                } else if (!allMeansEmpty) {
                  deselectAll()
                }
              }}
            />
            <label
              className={styles.label}
              htmlFor={`${title.toLowerCase()}_all`}
            >
              <b>Все {title}</b>
            </label>
          </div>
          {itemsAfterUpdate?.map((item: any) => {
            return (
              <div key={item.id}>
                <input
                  className={styles.input}
                  type="checkbox"
                  name={`filter_${title.toLowerCase()}`}
                  id={`${item.id}`}
                  value={item.id}
                  checked={selectedItems.some(
                    (selected: any) => selected.id === item.id
                  )}
                  onChange={(e) => handleChange(e, item)}
                />
                <label className={styles.label} htmlFor={`${item.id}`}>
                  {item.name}
                </label>
              </div>
            )
          })}
          <div style={{ textAlign: 'left' }}>
            <div
              className={classNames(
                styles.btn,
                styles.btnSmall2,
                styles.btnBlueOutline,
                styles.btnDropdownClose
              )}
              onClick={() => {
                getReports()
                setIsOpen(false)
              }}
            >
              Применить
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const DropdownForUser = ({
  title,
  isOpen,
  setIsOpen,
  selectedItems,
  items,
  handleChange,
  handleSelectAll,
  handleDeselectAll,
  setSelectedDrivers,
  getReports,
  columnClassName = 'col-6 col-sm-4 col-md-4 col-lg-4 col-xl-4',
  /** Всегда показывать эти роли в фильтре (даже если список пуст) */
  fixedRoles,
}: DropdownProps & { fixedRoles?: string[] }) => {
  const dispatch = useDispatch()
  const ref = useOutsideClick(() => {
    setIsOpen(false)
  })

  const [openRoles, setOpenRoles] = useState<Record<string, boolean>>({})

  const isAllSelected = selectedItems.length === 0

  const normalizeRole = (role: unknown) =>
    String(role || '')
      .toLowerCase()
      .trim()

  const ROLE_STUB_PREFIX = '__role__'

  const getRoleTitle = (role: string) => {
    switch (normalizeRole(role)) {
      case 'driver':
        return 'Водитель'
      case 'owner':
        return 'Владелец'
      case 'admin':
        return 'Админ'
      case 'client':
        return 'Клиент'
      default:
        return role
    }
  }

  const makeRoleStub = (role: string) => ({
    id: `${ROLE_STUB_PREFIX}${normalizeRole(role)}`,
    name: getRoleTitle(role),
    role: normalizeRole(role),
  })

  const isRoleStub = (item: any) =>
    String(item?.id || '').startsWith(ROLE_STUB_PREFIX)

  const isRoleStubSelected = (role: string) =>
    selectedItems.some(
      (item: any) =>
        isRoleStub(item) && normalizeRole(item.role) === normalizeRole(role)
    )

  const isRoleFullySelected = (role: string) => {
    const roleItems = items.filter(
      (item: any) => normalizeRole(item.role) === normalizeRole(role)
    )
    if (!roleItems.length) {
      return isRoleStubSelected(role)
    }
    return roleItems.every((roleItem: any) =>
      selectedItems.some((selected: any) => selected.id === roleItem.id)
    )
  }

  const toggleRole = (role: string) => {
    setOpenRoles((prevOpenRoles) => ({
      ...prevOpenRoles,
      [role]: !prevOpenRoles[role],
    }))
  }

  const ROLE_ORDER = ['driver', 'owner', 'admin', 'client']

  const itemsByRole = useMemo(() => {
    const grouped: Record<string, any[]> = {}
    ;(items || []).forEach((item: any) => {
      const role = normalizeRole(item?.role)
      if (!role) return
      if (!grouped[role]) grouped[role] = []
      grouped[role].push(item)
    })

    const roles = fixedRoles?.length
      ? fixedRoles
      : Object.keys(grouped).sort((a, b) => {
          const ai = ROLE_ORDER.indexOf(a)
          const bi = ROLE_ORDER.indexOf(b)
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
        })

    return roles
      .filter(
        (role) =>
          (grouped[role]?.length ?? 0) > 0 || fixedRoles?.includes(role)
      )
      .map((role) => ({
        role,
        items: grouped[role] || [],
      }))
  }, [items, fixedRoles])

  const selectAll = () => {
    handleSelectAll([])
  }

  const deselectAll = () => {
    handleDeselectAll()
  }

  const selectAllEmployeeByRole = (role: string) => {
    const roleNorm = normalizeRole(role)
    const roleItems = items.filter(
      (item: any) => normalizeRole(item.role) === roleNorm
    )

    // Пустая группа (например Админ без юзеров) — всё равно можно выбрать
    if (!roleItems.length) {
      const stub = makeRoleStub(roleNorm)
      if (selectedItems.length === 0) {
        handleSelectAll([stub])
        return
      }
      const withoutStub = selectedItems.filter(
        (item: any) =>
          !(isRoleStub(item) && normalizeRole(item.role) === roleNorm)
      )
      handleSelectAll([...withoutStub, stub])
      return
    }

    if (selectedItems.length === 0) {
      handleSelectAll(roleItems)
      return
    }
    const merged = selectedItems.filter(
      (item: any) =>
        !(isRoleStub(item) && normalizeRole(item.role) === roleNorm)
    )
    roleItems.forEach((roleItem: any) => {
      if (!merged.some((selected: any) => selected.id === roleItem.id)) {
        merged.push(roleItem)
      }
    })
    handleSelectAll(merged)
  }

  const deleteAllEmployeeByRole = (role: string) => {
    const roleNorm = normalizeRole(role)
    const roleItems = items.filter(
      (item: any) => normalizeRole(item.role) === roleNorm
    )

    // «Все» = пустой selectedItems → все, кроме этой роли
    if (selectedItems.length === 0) {
      if (!roleItems.length) {
        // из «Все» снимаем пустую роль → выбираем всех реальных юзеров
        handleSelectAll([...(items || [])])
        return
      }
      handleSelectAll(
        items.filter((item: any) => normalizeRole(item.role) !== roleNorm)
      )
      return
    }

    const roleItemIds = new Set(roleItems.map((item: any) => item.id))
    handleSelectAll(
      selectedItems.filter(
        (item: any) =>
          !roleItemIds.has(item.id) &&
          !(isRoleStub(item) && normalizeRole(item.role) === roleNorm)
      )
    )
  }

  const getTitle = getRoleTitle

  return (
    <div className={columnClassName}>
      <div className="d-lg-block mb-1">{title}</div>
      <div
        ref={ref}
        onClick={() => setIsOpen((prev: boolean) => !prev)}
        className={classNames(
          styles.dropdown,
          styles.dropdownMultipleSelect,
          'd-block',
          isOpen && styles.opened
        )}
        style={{ textAlign: 'left' }}
      >
        <div
          className={classNames(
            styles.btn,
            styles.btnSmall,
            styles.btnWide,
            styles.btnInput,
            styles.btnDropdown
          )}
        >
          {selectedItems.length
            ? selectedItems.map(({ name }: any) => name).join(', ')
            : `Все ${title}`}
        </div>
        <div
          className={styles.dropdownContent}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.scrollDropdown}>
            <div
              style={{ textAlign: 'left' }}
              className={styles.dropdownInputRole}
            >
              <input
                className={styles.input}
                type="checkbox"
                checked={isAllSelected}
                name={`filter_${title.toLowerCase()}_all`}
                id={`${title.toLowerCase()}_all`}
                onChange={(e) => {
                  if (e.target.checked) {
                    selectAll()
                  }
                }}
              />
              <label
                className={styles.label}
                htmlFor={`${title.toLowerCase()}_all`}
              >
                <b>Все {title}</b>
              </label>
            </div>

            {itemsByRole.map((group) => (
              <div key={group.role}>
                <div className={styles.dropdownInputRole}>
                  <input
                    className={styles.input}
                    checked={isRoleFullySelected(group.role)}
                    id={`${title.toLowerCase()}_${group.role}`}
                    type="checkbox"
                    value={group.role}
                    onChange={(e) => {
                      if (e.target.checked) {
                        selectAllEmployeeByRole(group.role)
                        if (group.items.length) {
                          setOpenRoles((prev) => ({
                            ...prev,
                            [group.role]: true,
                          }))
                        }
                      } else {
                        deleteAllEmployeeByRole(group.role)
                      }
                    }}
                  />
                  <label
                    className={styles.label}
                    htmlFor={`${title.toLowerCase()}_${group.role}`}
                  />
                  <div
                    className={styles.roles}
                    onClick={() => {
                      if (group.items.length) toggleRole(group.role)
                    }}
                  >
                    <b>{getTitle(group.role)}</b>
                  </div>
                </div>

                {openRoles[group.role] && group.items.length > 0 && (
                  <div>
                    {group.items.map((item: any) => (
                      <div key={item.id} className={styles.dropdownInput}>
                        <input
                          className={styles.input}
                          type="checkbox"
                          name={`filter_${title.toLowerCase()}`}
                          id={`${title.toLowerCase()}_user_${item.id}`}
                          value={item.id}
                          checked={selectedItems.some(
                            (selected: any) => selected.id === item.id
                          )}
                          onChange={(e) => handleChange(e, item)}
                        />
                        <label
                          className={styles.label}
                          htmlFor={`${title.toLowerCase()}_user_${item.id}`}
                        >
                          {item.name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'left' }}>
            <div
              className={classNames(
                styles.btn,
                styles.btnSmall2,
                styles.btnBlueOutline,
                styles.btnDropdownClose
              )}
              onClick={() => {
                getReports()
                setIsOpen(false)
              }}
            >
              Применить
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
interface DropdownStatusProps {
  title: string
  isOpen: boolean
  setIsOpen: any
  selectedItems: string[]
  items: string[]
  handleChange: any
  handleSelectAll: any
  handleDeselectAll: any
  getReports?: any
  columnClassName?: string
}

const DropdownStatus = ({
  title,
  isOpen,
  setIsOpen,
  selectedItems,
  items,
  handleChange,
  handleSelectAll,
  handleDeselectAll,
  getReports,
  columnClassName = 'col-6 col-sm-4 col-md-4 col-lg-4 col-xl-4',
}: DropdownStatusProps) => {
  const ref = useOutsideClick(() => {
    setIsOpen(false)
  })

  const selectAll = () => {
    handleSelectAll([])
  }

  const deselectAll = () => {
    handleDeselectAll()
  }

  const isAllSelected = selectedItems.length === 0

  const translations: { [key: string]: string } = REPORT_STATUS_FILTER_LABELS

  return (
    <div className={columnClassName}>
      <div className="d-lg-block mb-1">{title}</div>
      <div
        ref={ref}
        onClick={() => setIsOpen((prev: boolean) => !prev)}
        className={classNames(
          styles.dropdown,
          styles.dropdownMultipleSelect,
          'd-block',
          isOpen && styles.opened
        )}
        style={{ textAlign: 'left' }}
      >
        <div
          className={classNames(
            styles.btn,
            styles.btnSmall,
            styles.btnWide,
            styles.btnInput,
            styles.btnDropdown
          )}
        >
          {selectedItems.length
            ? selectedItems.map((item) => translations[item]).join(', ')
            : `Все ${title}`}
        </div>
        <div
          className={styles.dropdownContent}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ textAlign: 'left' }}>
            <input
              className={styles.input}
              type="checkbox"
              name={`filter_${title.toLowerCase()}_all`}
              id={`${title.toLowerCase()}_all`}
              checked={isAllSelected}
              onChange={(e) => {
                if (e.target.checked) {
                  selectAll()
                }
              }}
            />
            <label
              className={styles.label}
              htmlFor={`${title.toLowerCase()}_all`}
            >
              <b>Все {title}</b>
            </label>
          </div>
          {items.map((item, index) => (
            <div key={index}>
              <input
                className={styles.input}
                type="checkbox"
                name={`filter_${title.toLowerCase()}`}
                id={`${item}`}
                value={item}
                checked={selectedItems.includes(item)}
                onChange={(e) => handleChange(e, item)}
              />
              <label className={styles.label} htmlFor={`${item}`}>
                {translations[item]}
              </label>
            </div>
          ))}
          <div style={{ textAlign: 'left' }}>
            <div
              className={classNames(
                styles.btn,
                styles.btnSmall2,
                styles.btnBlueOutline,
                styles.btnDropdownClose
              )}
              onClick={() => {
                getReports()
                setIsOpen(false)
              }}
            >
              Применить
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ReportsFilter({
  selectedTrucks,
  selectedDrivers,
  selectedStatus,
  getReports,
  isVisibleForm,
}: any) {
  const dispatch = useDispatch()
  const [isOpenTrucks, setIsOpenTrucks] = useState<boolean>(false)
  const [isOpenDrivers, setIsOpenDrivers] = useState<boolean>(false)
  const [isOpenStatus, setIsOpenStatus] = useState<boolean>(false)

  const commercialCars = useSelector(commercialCarsSelector)
  const { users } = useSelector(usersSelector)

  const handleChangeCars = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: Option
  ) => {
    const { checked } = event.target

    if (checked) {
      const alreadySelected = selectedTrucks.some(
        (option: Option) => option.id === item.id
      )
      if (alreadySelected) return

      dispatch(
        setSelectedTrucks(
          selectedTrucks.length === 0 ? [item] : [...selectedTrucks, item]
        )
      )
      return
    }

    dispatch(
      setSelectedTrucks(
        selectedTrucks.filter((option: Option) => option.id !== item.id)
      )
    )
  }

  const handleChangeStatus = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: string
  ) => {
    const { checked } = event.target

    if (checked) {
      if (selectedStatus.includes(item)) return
      dispatch(
        setSelectedStatus(
          selectedStatus.length === 0 ? [item] : [...selectedStatus, item]
        )
      )
      return
    }

    dispatch(
      setSelectedStatus(
        selectedStatus.filter((selectedItem: string) => selectedItem !== item)
      )
    )
  }

  const handleSelectDriver = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: Option
  ) => {
    const { checked } = event.target

    if (checked) {
      const alreadySelected = selectedDrivers.some(
        (driver: Option) => driver.id === item.id
      )
      if (alreadySelected) return

      dispatch(
        setSelectedDrivers(
          selectedDrivers.length === 0 ? [item] : [...selectedDrivers, item]
        )
      )
      return
    }

    dispatch(
      setSelectedDrivers(
        selectedDrivers.filter((driver: Option) => driver.id !== item.id)
      )
    )
  }

  const handleSelectAllTrucks = () => {
    dispatch(setSelectedTrucks([]))
  }

  const handleDeselectAllTrucks = () => {
    dispatch(setSelectedTrucks([]))
  }

  const handleSelectAllStatus = (items: string[]) => {
    dispatch(setSelectedStatus(items))
  }

  const handleDeselectAllStatus = () => {
    dispatch(setSelectedStatus([]))
  }

  const handleSelectAllDrivers = (items: Option[]) => {
    dispatch(setSelectedDrivers(items))
  }

  const handleDeselectAllDrivers = () => {
    dispatch(setSelectedDrivers([]))
  }

  return (
    <form
      className={classNames(
        isVisibleForm ? styles.filterForm : '',
        styles.filterReports,
        'mb-3'
      )}
      id="filter_form"
    >
      <div
        className={classNames(
          styles.tableFilterPanel,
          styles.small,
          'row',
          'align-items-end'
        )}
      >
        <Dropdown
          title="Машины"
          isOpen={isOpenTrucks}
          setIsOpen={setIsOpenTrucks}
          selectedItems={selectedTrucks}
          items={commercialCars}
          handleChange={handleChangeCars}
          handleSelectAll={handleSelectAllTrucks}
          handleDeselectAll={handleDeselectAllTrucks}
          getReports={getReports}
          allMeansEmpty
        />
        <DropdownForUser
          title="Водители"
          isOpen={isOpenDrivers}
          setIsOpen={setIsOpenDrivers}
          selectedItems={selectedDrivers}
          items={users.filter(
            (item) =>
              item.role === 'driver' ||
              item.role === 'owner' ||
              item.role === 'admin'
          )}
          handleChange={handleSelectDriver}
          handleSelectAll={handleSelectAllDrivers}
          handleDeselectAll={handleDeselectAllDrivers}
          setSelectedDrivers={setSelectedDrivers}
          getReports={getReports}
        />
        <DropdownStatus
          title="Статусы"
          isOpen={isOpenStatus}
          setIsOpen={setIsOpenStatus}
          selectedItems={selectedStatus}
          items={[...REPORT_STATUS_FILTER_ITEMS]}
          handleChange={handleChangeStatus}
          handleSelectAll={handleSelectAllStatus}
          handleDeselectAll={handleDeselectAllStatus}
          getReports={getReports}
        />
      </div>
    </form>
  )
}

export function ReportsFilterCommon({
  selectedUsersCommon = [],
  selectedStatusCommon = [],
  getReports,
  isVisibleForm,
}: any) {
  const dispatch = useDispatch()
  const [isOpenUsers, setIsOpenUsers] = useState(false)
  const [isOpenStatus, setIsOpenStatus] = useState(false)
  const { users } = useSelector(usersSelector)

  // Водитель / Владелец / Админ (+ текущий пользователь из сессии, если его нет в списке)
  const staffUsers = useMemo(() => {
    const list: any[] = (users || []).filter((item: any) => {
      const role = String(item?.role || '')
        .toLowerCase()
        .trim()
      return role === 'driver' || role === 'owner' || role === 'admin'
    })

    try {
      const raw = localStorage.getItem('userInfo')
      const info = raw ? JSON.parse(raw) : null
      const role = String(info?.role || '')
        .toLowerCase()
        .trim()
      if (
        info?.id &&
        (role === 'driver' || role === 'owner' || role === 'admin') &&
        !list.some((u) => String(u.id) === String(info.id))
      ) {
        list.push({
          id: info.id,
          name: info.name || info.login || 'Без имени',
          role,
          active: true,
        })
      }
    } catch {
      // ignore
    }

    return list
  }, [users])
  const usersSelected = selectedUsersCommon || []
  const statusSelected = selectedStatusCommon || []

  const handleSelectUser = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: Option
  ) => {
    const { checked } = event.target
    if (checked) {
      if (usersSelected.some((option: Option) => option.id === item.id)) return
      dispatch(
        setSelectedUsersCommon(
          usersSelected.length === 0 ? [item] : [...usersSelected, item]
        )
      )
      return
    }
    dispatch(
      setSelectedUsersCommon(
        usersSelected.filter((option: Option) => option.id !== item.id)
      )
    )
  }

  const handleChangeStatus = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: string
  ) => {
    const { checked } = event.target
    if (checked) {
      if (statusSelected.includes(item)) return
      dispatch(
        setSelectedStatusCommon(
          statusSelected.length === 0 ? [item] : [...statusSelected, item]
        )
      )
      return
    }
    dispatch(
      setSelectedStatusCommon(
        statusSelected.filter((status: string) => status !== item)
      )
    )
  }

  return (
    <form
      className={classNames(
        isVisibleForm ? styles.filterForm : '',
        styles.filterReports,
        styles.filterReportsCommon,
        'mb-3'
      )}
      id="filter_form_common"
    >
      <div
        className={classNames(
          styles.tableFilterPanel,
          styles.small,
          'row',
          'align-items-end'
        )}
      >
        <DropdownForUser
          title="Юзеры"
          isOpen={isOpenUsers}
          setIsOpen={setIsOpenUsers}
          selectedItems={usersSelected}
          items={staffUsers}
          handleChange={handleSelectUser}
          handleSelectAll={(items: Option[]) =>
            dispatch(setSelectedUsersCommon(items))
          }
          handleDeselectAll={() => dispatch(setSelectedUsersCommon([]))}
          getReports={getReports}
          columnClassName="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6"
          fixedRoles={['driver', 'owner', 'admin']}
        />
        <DropdownStatus
          title="Статусы"
          isOpen={isOpenStatus}
          setIsOpen={setIsOpenStatus}
          selectedItems={statusSelected}
          items={[...REPORT_COMMON_STATUS_FILTER_ITEMS]}
          handleChange={handleChangeStatus}
          handleSelectAll={(items: string[]) =>
            dispatch(setSelectedStatusCommon(items))
          }
          handleDeselectAll={() => dispatch(setSelectedStatusCommon([]))}
          getReports={getReports}
          columnClassName="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-6"
        />
      </div>
    </form>
  )
}

export default ReportsFilter
